import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { operationsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import toast from 'react-hot-toast';
import {
  HiOutlinePencil, HiOutlineTrash, HiOutlineCheck, HiOutlineX,
  HiOutlinePlus, HiOutlineSearch, HiOutlineRefresh, HiOutlineDatabase
} from 'react-icons/hi';

const BLANK_ROW = {
  modelNumber: '', pitch: '', ratePerMeter: '', innerSize: '', radius: '', 
  designType: 'OP', linksPerMeter: '', description: '',
  hs: 'N/A', vs: 'N/A', sr: 'N/A', outerSize: '',
  unit: 'pcs', name: 'Nylon Drag Chain', type: 'finished_good',
  warehouseLocation: 'N/A', almirahCatalog: 'N/A', rackShelfCatalog: 'N/A'
};

const SendToInventoryModal = ({ template, onClose, onConfirm }) => {
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState(0);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      await onConfirm(template._id, { quantity: qty, unitPrice: price });
      onClose();
    } catch {
      // error already toasted
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px',
        padding: '28px', width: '380px', boxShadow: '0 24px 60px rgba(0,0,0,0.4)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span style={{ fontSize: '1.5rem' }}>📦</span>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Send to Store Inventory</h3>
        </div>
        <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', margin: '0 0 20px' }}>
          Adding <strong style={{ color: 'var(--primary)' }}>{template.modelNumber}</strong>{' '}
          ({template.innerSize || '—'} | {template.radius || '—'}) to Store Inventory.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Quantity</label>
            <input className="form-input" type="number" min={1} value={qty}
              onChange={e => setQty(Number(e.target.value))} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Unit Price (₹)</label>
            <input className="form-input" type="number" min={0} value={price}
              onChange={e => setPrice(Number(e.target.value))} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending} style={{ background: '#22c55e', border: 'none' }}>
            {sending ? 'Sending...' : 'Confirm & Add to Stock'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function UniversalModelInfo() {
  const { user } = useAuth();
  const { settings } = useOrg();
  const subIndustryId = user?.organizationId?.subIndustryId || 'drag_chain';
  const isDragChain = subIndustryId === 'drag_chain';

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRow, setNewRow] = useState(BLANK_ROW);
  const [addingSaving, setAddingSaving] = useState(false);
  const [sendModal, setSendModal] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  const filtered = useMemo(() => {
    if (!search.trim()) return templates;
    const q = search.toLowerCase();
    return templates.filter(t =>
      (t.modelNumber && t.modelNumber.toLowerCase().includes(q)) ||
      (t.innerSize && t.innerSize.toLowerCase().includes(q)) ||
      (t.radius && t.radius.toLowerCase().includes(q)) ||
      (t.designType && t.designType.toLowerCase().includes(q)) ||
      (t.description && t.description.toLowerCase().includes(q)) ||
      (t.warehouseLocation && t.warehouseLocation.toLowerCase().includes(q)) ||
      (t.almirahCatalog && t.almirahCatalog.toLowerCase().includes(q)) ||
      (t.rackShelfCatalog && t.rackShelfCatalog.toLowerCase().includes(q))
    );
  }, [templates, search]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await operationsAPI.getProductTemplates();
      setTemplates(res.data?.data || []);
    } catch (err) {
      toast.error('Failed to load model catalogue');
    } finally {
      setLoading(false);
    }
  }, []);


  const startEdit = (t) => { setEditingId(t._id); setEditForm({ ...t }); };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await operationsAPI.updateProductTemplate(editingId, editForm);
      toast.success('Model updated successfully');
      setEditingId(null);
      await fetchTemplates();
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this model entry permanently?')) return;
    setDeletingId(id);
    try {
      await operationsAPI.deleteProductTemplate(id);
      toast.success('Model deleted');
      setTemplates(prev => prev.filter(t => t._id !== id));
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddRow = async () => {
    if (!newRow.modelNumber.trim()) { toast.error('Model Number is required'); return; }
    setAddingSaving(true);
    try {
      await operationsAPI.createProductTemplate(newRow);
      toast.success('New model added');
      setShowAddRow(false);
      setNewRow({ ...BLANK_ROW });
      await fetchTemplates();
    } catch {
      toast.error('Failed to add model');
    } finally {
      setAddingSaving(false);
    }
  };

  const handleSendToInventory = async (id, data) => {
    await operationsAPI.sendTemplateToInventory(id, data);
    toast.success('Sent to Store Inventory!');
  };

  const cellStyle = {
    padding: '10px 14px', fontSize: '0.83rem', borderBottom: '1px solid var(--border)',
    color: 'var(--text-primary)', whiteSpace: 'nowrap', verticalAlign: 'middle'
  };
  const inputCellStyle = {
    background: 'rgba(255,255,255,0.06)', border: '1px solid var(--primary)',
    borderRadius: '6px', color: 'var(--text-primary)', padding: '5px 8px',
    fontSize: '0.82rem', width: '100%', outline: 'none'
  };
  const thStyle = {
    padding: '12px 14px', fontSize: '0.72rem', fontWeight: 700,
    color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px',
    background: 'rgba(249,115,22,0.06)', whiteSpace: 'nowrap',
    borderBottom: '2px solid rgba(249,115,22,0.2)'
  };

  const COLUMNS = [
    { key: 'modelNumber',   label: 'Model No.',            width: '140px' },
    { key: 'pitch',         label: 'Pitch (mm)',           width: '105px' },
    { key: 'ratePerMeter',  label: 'Rate / M (₹)',         width: '120px' },
    { key: 'linksPerMeter', label: 'NO. OF LINKS / METER', width: '150px' },
    { key: 'innerSize',     label: 'Inner width',          width: '120px' },
    { key: 'radius',        label: 'Bending Radius',       width: '130px' },
    { key: 'designType',    label: 'Open/Close/Semi close', width: '170px' },
    { key: 'description',   label: 'Description',          width: '200px' },
    { key: 'warehouseLocation', label: 'Warehouse Locations', width: '180px' },
    { key: 'almirahCatalog', label: 'Almirah / Cupboards Catalog', width: '200px' },
    { key: 'rackShelfCatalog', label: 'Rack / Shelf (S.No) Catalog', width: '200px' },
  ];

  if (!isDragChain) {
    return (
      <DashboardLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: '12px' }}>
          <HiOutlineDatabase size={48} color="var(--text-dim)" />
          <p style={{ color: 'var(--text-dim)', fontSize: '1rem' }}>Universal Model Info is only available for Drag Chain organizations.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {sendModal && (
        <SendToInventoryModal
          template={sendModal}
          onClose={() => setSendModal(null)}
          onConfirm={handleSendToInventory}
        />
      )}

      <div style={{ padding: '24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.8rem' }}>📋</span> Universal Model Info
            </h1>
            <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: '0.87rem' }}>
              Complete drag chain model catalogue — edit inline, delete, or push directly to Store Inventory
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button onClick={fetchTemplates} title="Refresh" style={{
              padding: '9px', border: '1px solid var(--border)', background: 'transparent',
              borderRadius: '8px', color: 'var(--text-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center'
            }}>
              <HiOutlineRefresh size={18} />
            </button>
            <button onClick={() => { setShowAddRow(true); setNewRow({ ...BLANK_ROW }); }} style={{
              padding: '9px 16px', background: 'var(--primary)', border: 'none',
              borderRadius: '8px', color: '#fff', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem'
            }}>
              <HiOutlinePlus size={18} /> Add Model
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px',
          padding: '12px 18px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px'
        }}>
          <HiOutlineSearch size={18} color="var(--text-dim)" />
          <input type="text" placeholder="Search by model no., size, radius, VS, HS, SR..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ background: 'transparent', border: 'none', outline: 'none', flex: 1, fontSize: '0.9rem', color: 'var(--text-primary)' }} />
          {search && (
            <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', display: 'flex' }}>
              <HiOutlineX size={16} />
            </button>
          )}
          <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>

        {/* Table */}
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px',
          overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.12)'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1500px' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: '40px', textAlign: 'center' }}>#</th>
                  {COLUMNS.map(col => (
                    <th key={col.key} style={{ ...thStyle, width: col.width }}>{col.label}</th>
                  ))}
                  <th style={{ ...thStyle, minWidth: '230px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* ADD NEW ROW */}
                {showAddRow && (
                  <tr style={{ background: 'rgba(249,115,22,0.06)' }}>
                    <td style={{ ...cellStyle, textAlign: 'center', color: 'var(--primary)', fontWeight: 700 }}>✦</td>
                    {COLUMNS.map(col => (
                      <td key={col.key} style={cellStyle}>
                        {col.key === 'designType' ? (
                          <select style={inputCellStyle} value={newRow[col.key]}
                            onChange={e => setNewRow(p => ({ ...p, [col.key]: e.target.value }))}>
                            <option value="OP">OP (Open)</option>
                            <option value="CP">CP (Closed)</option>
                            <option value="SM">SM (Semi)</option>
                          </select>
                        ) : col.key === 'warehouseLocation' ? (
                          <select style={inputCellStyle} value={newRow[col.key] || 'N/A'} onChange={e => setNewRow(p => ({ ...p, [col.key]: e.target.value }))}>
                            <option value="N/A">N/A</option>
                            {(settings?.warehouses || []).map(w => <option key={w} value={w}>{w}</option>)}
                          </select>
                        ) : col.key === 'almirahCatalog' ? (
                          <select style={inputCellStyle} value={newRow[col.key] || 'N/A'} onChange={e => setNewRow(p => ({ ...p, [col.key]: e.target.value }))}>
                            <option value="N/A">N/A</option>
                            {(settings?.almirahs || []).map(a => <option key={a} value={a}>{a}</option>)}
                          </select>
                        ) : col.key === 'rackShelfCatalog' ? (
                          <select style={inputCellStyle} value={newRow[col.key] || 'N/A'} onChange={e => setNewRow(p => ({ ...p, [col.key]: e.target.value }))}>
                            <option value="N/A">N/A</option>
                            {(settings?.racks || []).map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        ) : (
                          <input style={inputCellStyle} value={newRow[col.key] || ''}
                            placeholder={col.label}
                            onChange={e => setNewRow(p => ({ ...p, [col.key]: e.target.value }))} />
                        )}
                      </td>
                    ))}
                    <td style={{ ...cellStyle, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button onClick={handleAddRow} disabled={addingSaving} style={{
                          padding: '6px 14px', background: 'var(--primary)', border: 'none',
                          borderRadius: '6px', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.8rem',
                          display: 'flex', alignItems: 'center', gap: '4px'
                        }}>
                          <HiOutlineCheck size={14} /> {addingSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={() => setShowAddRow(false)} style={{
                          padding: '6px 10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                          borderRadius: '6px', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center'
                        }}>
                          <HiOutlineX size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}

                {loading ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 2} style={{ textAlign: 'center', padding: '48px', color: 'var(--text-dim)' }}>
                      Loading model catalogue...
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 2} style={{ textAlign: 'center', padding: '48px', color: 'var(--text-dim)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <HiOutlineDatabase size={40} />
                        <span>No models found. Click "Add Model" to get started.</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((t, index) => {
                  const idx = (page - 1) * ITEMS_PER_PAGE + index;
                  const isEditing = editingId === t._id;
                  const isDeleting = deletingId === t._id;
                  return (
                    <tr key={t._id} style={{
                      background: isEditing
                        ? 'rgba(249,115,22,0.04)'
                        : idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.012)',
                      transition: 'background 0.15s'
                    }}>
                      <td style={{ ...cellStyle, textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.75rem' }}>
                        {idx + 1}
                      </td>

                      {COLUMNS.map(col => (
                        <td key={col.key} style={cellStyle}>
                          {isEditing ? (
                            col.key === 'designType' ? (
                              <select style={inputCellStyle} value={editForm[col.key] || ''}
                                onChange={e => setEditForm(p => ({ ...p, [col.key]: e.target.value }))}>
                                <option value="OP">OP (Open)</option>
                                <option value="CP">CP (Closed)</option>
                                <option value="SM">SM (Semi)</option>
                                <option value="N/A">N/A</option>
                              </select>
                            ) : col.key === 'warehouseLocation' ? (
                              <select style={inputCellStyle} value={editForm[col.key] || 'N/A'} onChange={e => setEditForm(p => ({ ...p, [col.key]: e.target.value }))}>
                                <option value="N/A">N/A</option>
                                {(settings?.warehouses || []).map(w => <option key={w} value={w}>{w}</option>)}
                              </select>
                            ) : col.key === 'almirahCatalog' ? (
                              <select style={inputCellStyle} value={editForm[col.key] || 'N/A'} onChange={e => setEditForm(p => ({ ...p, [col.key]: e.target.value }))}>
                                <option value="N/A">N/A</option>
                                {(settings?.almirahs || []).map(a => <option key={a} value={a}>{a}</option>)}
                              </select>
                            ) : col.key === 'rackShelfCatalog' ? (
                              <select style={inputCellStyle} value={editForm[col.key] || 'N/A'} onChange={e => setEditForm(p => ({ ...p, [col.key]: e.target.value }))}>
                                <option value="N/A">N/A</option>
                                {(settings?.racks || []).map(r => <option key={r} value={r}>{r}</option>)}
                              </select>
                            ) : (
                              <input style={inputCellStyle} value={editForm[col.key] || ''}
                                onChange={e => setEditForm(p => ({ ...p, [col.key]: e.target.value }))} />
                            )
                          ) : (
                            <span style={{
                              color: col.key === 'modelNumber' ? 'var(--primary)' : 'var(--text-primary)',
                              fontWeight: col.key === 'modelNumber' ? 700 : 400,
                              fontFamily: col.key === 'modelNumber' ? 'monospace' : 'inherit',
                              fontSize: col.key === 'modelNumber' ? '0.85rem' : '0.83rem'
                            }}>
                              {t[col.key] || <span style={{ color: 'rgba(255,255,255,0.2)', fontStyle: 'italic', fontFamily: 'inherit' }}>—</span>}
                            </span>
                          )}
                        </td>
                      ))}

                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button onClick={saveEdit} disabled={saving} style={{
                              padding: '6px 14px', background: 'var(--primary)', border: 'none',
                              borderRadius: '6px', color: '#fff', fontWeight: 700, cursor: 'pointer',
                              fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px'
                            }}>
                              <HiOutlineCheck size={14} /> {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button onClick={cancelEdit} style={{
                              padding: '6px 10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                              borderRadius: '6px', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center'
                            }}>
                              <HiOutlineX size={14} />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                            <button onClick={() => startEdit(t)} title="Edit" style={{
                              padding: '6px', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)',
                              borderRadius: '6px', color: '#818cf8', cursor: 'pointer', display: 'flex', alignItems: 'center'
                            }}>
                              <HiOutlinePencil size={15} />
                            </button>
                            <button onClick={() => handleDelete(t._id)} disabled={isDeleting} title="Delete" style={{
                              padding: '6px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)',
                              borderRadius: '6px', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center',
                              opacity: isDeleting ? 0.5 : 1
                            }}>
                              <HiOutlineTrash size={15} />
                            </button>
                            <button onClick={() => setSendModal(t)} title="Send to Store Inventory" style={{
                              padding: '6px 10px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
                              borderRadius: '6px', color: '#22c55e', cursor: 'pointer', display: 'flex', alignItems: 'center',
                              gap: '5px', fontSize: '0.77rem', fontWeight: 700, whiteSpace: 'nowrap'
                            }}>
                              📦 Send to Inventory
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filtered.length > 0 && (
            <div style={{
              padding: '10px 18px', borderTop: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontSize: '0.79rem', color: 'var(--text-dim)'
            }}>
              <span>
                Total: <strong style={{ color: 'var(--text-primary)' }}>{templates.length}</strong> model entries 
                {search && <span> (Showing <strong style={{ color: 'var(--text-primary)' }}>{filtered.length}</strong> results for "{search}")</span>}
              </span>
              
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button 
                  onClick={() => setPage(p => Math.max(1, p - 1))} 
                  disabled={page === 1}
                  style={{ 
                    padding: '4px 10px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: '4px', color: page === 1 ? 'var(--text-dim)' : 'var(--text-primary)', cursor: page === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Prev
                </button>
                <span>Page {page} of {Math.ceil(filtered.length / ITEMS_PER_PAGE)}</span>
                <button 
                  onClick={() => setPage(p => Math.min(Math.ceil(filtered.length / ITEMS_PER_PAGE), p + 1))} 
                  disabled={page === Math.ceil(filtered.length / ITEMS_PER_PAGE) || filtered.length === 0}
                  style={{ 
                    padding: '4px 10px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: '4px', color: page === Math.ceil(filtered.length / ITEMS_PER_PAGE) ? 'var(--text-dim)' : 'var(--text-primary)', cursor: page === Math.ceil(filtered.length / ITEMS_PER_PAGE) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
